"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import {
  CircleCheckIcon,
  CircleXIcon,
  InfoIcon,
  TriangleAlertIcon,
  Loader2Icon,
} from "lucide-react"

/**
 * Where the corner tick sits: top right, just under the top bar.
 *
 * The bar is `h-14` inside the dashboard's `p-1.5` frame (see AppHeader and
 * the dashboard layout), so the pill starts a small gap below that and never
 * covers the theme toggle or the page actions. The styling itself (pill
 * shape, motion, one-line size) lives in globals.css under `.cn-toast`,
 * because sonner's own rules out-rank utility classes.
 */
const OFFSET = {
  top: "calc(0.375rem + 3.5rem + 0.5rem)",
  right: "1rem",
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="top-right"
      // A new toast replaces the one on screen instead of stacking under it.
      visibleToasts={1}
      gap={8}
      offset={OFFSET}
      mobileOffset={OFFSET}
      icons={{
        success: <CircleCheckIcon className="text-brand size-4" />,
        info: <InfoIcon className="text-muted-foreground size-4" />,
        warning: <TriangleAlertIcon className="text-destructive size-4" />,
        error: <CircleXIcon className="text-destructive size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          "--width": "20rem",
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
